import { ApiError, fieldErrorsFrom, isEnvelope } from './envelope';
import { client } from './generated/client.gen';
import { viewingScope } from './viewing-scope';

/**
 * Teaches the generated client to fail with a proper {@link ApiError}.
 *
 * By default the client throws the raw parsed error body, which is the backend's
 * `{ message, data, error }` envelope and carries no HTTP status. The status is
 * what tells the portal apart a "you typed it wrong" (422) from a "you are not
 * allowed" (403) from a "the server broke" (500), so it must not be lost.
 *
 * This interceptor runs where both the body and the response are still in scope,
 * and replaces the thrown value with something that has both.
 *
 * Import this module once at start-up (see src/main.tsx).
 */
client.interceptors.error.use((error, response) => {
  if (error instanceof ApiError) return error;

  // `response` is typed as possibly missing because a request can fail before
  // one exists at all, for example when the network is down.
  const status = response?.status ?? 0;

  if (isEnvelope(error)) {
    return new ApiError(
      status,
      error.error ?? null,
      error.message ?? null,
      // The structured field list, the day the backend sends one.
      fieldErrorsFrom(error.data),
      error.data,
    );
  }

  return new ApiError(
    status,
    typeof error === 'string' ? error : null,
    response?.statusText || null,
  );
});

/** Said when a change is attempted while staff are viewing an organization. */
export const READ_ONLY_MESSAGE =
  'Changes are not possible while viewing an organization as Activist staff.';

/** The methods that only read. Everything else changes something. */
const READS = new Set(['GET', 'HEAD']);

/**
 * Sign-in and sign-out, which must work whatever mode the portal is in.
 * Signing out is a POST, and a view must never be what keeps a session alive.
 */
const ALWAYS_ALLOWED = '/activist-arc/organization/auth/';

/**
 * Reads sent as a POST because they carry a body, so they change nothing, and
 * staff viewing an organization may send them: the ARC's Preview applies the
 * working copy and keeps none of it (organization-arc, D16); a custom page's
 * preview is rendered and stored nowhere (custom-pages D16, P22).
 */
const READS_SENT_AS_POST = new Set([
  '/activist-arc/organization/portal/arc/preview',
  '/activist-arc/organization/portal/pages/preview',
]);

/**
 * While Activist staff view an organization, nothing leaves the browser that
 * would change anything.
 *
 * The pages hide their save and create controls as well; this is what makes
 * that a guarantee rather than a hope, so a control one page forgot to hide
 * still cannot write. It is not the security boundary - the backend refuses
 * every write from a staff credential on the portal routes regardless - but it
 * is the earlier and clearer of the two refusals.
 *
 * It reads the view from its store rather than from React, because it runs
 * outside any component. Token renewal uses plain `fetch` and never passes
 * through here.
 */
client.interceptors.request.use((request) => {
  if (READS.has(request.method.toUpperCase())) return request;
  if (!viewingScope.get()) return request;
  const { pathname } = new URL(request.url);
  if (pathname.startsWith(ALWAYS_ALLOWED)) return request;
  if (READS_SENT_AS_POST.has(pathname)) return request;
  throw new ApiError(0, READ_ONLY_MESSAGE, null);
});

export { client };
